import { Box, Button, IconButton } from '@mui/material'
import { useState } from 'react'
import Lightbox, { RenderSlideContainerProps, Slide } from 'yet-another-react-lightbox'
import Inline from 'yet-another-react-lightbox/plugins/inline'
import Video from 'yet-another-react-lightbox/plugins/video'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import Captions from 'yet-another-react-lightbox/plugins/captions'
import {
  carouselContainer,
  carouselImage,
  carouselDetails,
  closeButton,
  carouselCaption,
  viewDetailButton,
} from './RiskMapCarousel.styles'

import './RiskMapCarousel.styles.css'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/captions.css'
import { Icon } from '@app/components/Atoms/Icon'
import { Branding, RiskMapSlide } from '@app/components/Risk/types'
import { Attribution, CalendarMonth } from '@mui/icons-material'

export interface RiskMapCarouselProps {
  slides: RiskMapSlide[]
  branding: Branding
  handleClose: () => void
}

export const RiskMapCarousel = ({ slides, branding, handleClose }: RiskMapCarouselProps) => {
  const carouselSlideContainer = ({ slide, children }: RenderSlideContainerProps) => {
    const riskSlide = slide as RiskMapSlide

    // WARNING! The css property doesn't work inside a mapbox popup!!
    // Have to use the style property instead.
    // Theme doesn't work either
    return (
      <Box style={carouselContainer}>
        <Box style={carouselImage}>{children}</Box>
        <Box style={carouselDetails}>
          <span style={carouselCaption}>{riskSlide.caption}</span>
          {riskSlide.dateTaken && (
            <span>
              <CalendarMonth className="popup-icon" /> Date taken:{' '}
              {new Date(riskSlide.dateTaken).toLocaleDateString()}
            </span>
          )}
          {riskSlide.attribution && (
            <span>
              <Attribution className="popup-icon" /> Attribution: {riskSlide.attribution}
            </span>
          )}
          <Box style={{ alignItems: 'right', width: '100%', display: 'flex', height: '100%' }}>
            <Box style={{ width: '100%' }}></Box>
          </Box>
        </Box>
      </Box>
    )
  }

  // As videos playing in popup bubbles causes problems generate a separate slide array for the carousel
  // which replaces video types with image using the videos's poster
  const carouselSlides = slides.map((i) => {
    if (i.type == 'video')
      return {
        caption: i.caption,
        attribution: i.attribution,
        dateTaken: i.dateTaken,
        description: i.description,
        src: i.poster ?? '',
        type: 'image',
      } as Slide

    return {
      caption: i.caption,
      attribution: i.attribution,
      dateTaken: i.dateTaken,
      description: i.description,
      src: i.src,
      type: 'image',
    } as Slide
  })

  const lightBoxSlides: Slide[] = slides.map((i) => {
    const flatDateTaken = i.dateTaken
      ? `Date taken: ${new Date(i.dateTaken).toLocaleDateString()} `
      : ''
    const flatAttribution = i.attribution ? `Attribution: ${i.attribution}  ` : ''
    const firstLine = flatDateTaken + flatAttribution
    const flatDescription = firstLine ? `${firstLine}\n${i.description}` : i.description

    if (i.type == 'video')
      return {
        type: 'video',
        title: i.caption,
        description: flatDescription,
        sources: [{ src: i.src, type: `video/${i.videoType}` }],
        autoPlay: true,
      } as Slide

    return {
      type: 'image',
      title: i.caption,
      description: flatDescription,
      src: i.src,
    } as Slide
  })

  const [lightBoxOpen, setLightBoxOpen] = useState(false)
  const [lightBoxIndex, setLightBoxIndex] = useState(0)

  // I don't fully understand what's going on here but it seems to keep both LightBox instances in sync
  // Copied from https://stackblitz.com/edit/yet-another-react-lightbox-examples?file=src%2Fexamples%2FCarousel.tsx&initialPath=/examples/carousel
  const updateIndex =
    (when: boolean) =>
    ({ index: current }: { index: number }) => {
      if (when === lightBoxOpen) {
        setLightBoxIndex(current)
      }
    }

  return (
    <Box
      style={{
        borderRadius: '20px',
        position: 'relative',
        boxShadow: '0px 0px 24px rgba(0, 0, 0, 0.25)',
        overflow: 'hidden',
      }}
    >
      {/* LightBox used as a carousel in the popup bubble */}
      <Lightbox
        index={lightBoxIndex}
        slides={carouselSlides}
        plugins={[Inline]}
        on={{
          view: updateIndex(false),
          click: () => setLightBoxOpen(true),
        }}
        styles={{
          container: { background: '#FBFBFB' },
          root: { height: '100%' },
        }}
        carousel={{
          padding: 0,
          spacing: 0,
          imageFit: 'cover',
        }}
        inline={{
          style: {
            height: '387px',
          },
        }}
        render={{
          slideContainer: carouselSlideContainer,
        }}
        toolbar={{
          buttons: [
            <IconButton style={closeButton} onClick={handleClose}>
              <Icon iconName="Close" size="small" />
            </IconButton>,
            <Button
              onClick={() => setLightBoxOpen(true)}
              style={{ ...viewDetailButton, color: branding.secondary }}
            >
              View&nbsp;detail
            </Button>,
          ],
        }}
      />

      {/* LightBox to show fullscreen images */}
      <Lightbox
        plugins={[Video, Zoom, Captions]}
        open={lightBoxOpen}
        close={() => setLightBoxOpen(false)}
        index={lightBoxIndex}
        slides={lightBoxSlides}
        on={{ view: updateIndex(true) }}
        controller={{ closeOnBackdropClick: true }}
      />
    </Box>
  )
}
